import React, { useId, useState } from 'react';

import {
  AlineTransforms,
  getIsSameBookmarkName,
  getIsValidBookmarkName,
  MAX_BOOKMARK_NAME_LENGTH,
} from '@aline/common';
import {
  DialogActions,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  List,
  ListItemButton,
  RadioGroup,
  Typography,
} from '@mui/material';
import { AlineInput } from 'src/AlineInput';
import { AlineButton, AlineDialog, AlineRadio } from 'src/components';
import { ClientTransforms } from 'src/editor/transforms';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { NotificationsActionType, useNotificationDispatch } from 'src/notifications';
import { useEditingDisabled } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

import type { BookmarkSortOrder } from './use-bookmarks';
import {
  BOOKMARK_SORT_ORDER_LABELS,
  BOOKMARK_SORT_ORDERS,
  isBookmarkSortOrder,
  useBookmarks,
} from './use-bookmarks';

interface BookmarkDialogProps {
  handleClose: () => void;
  open: boolean;
}

/** Matches the Insert Cross-reference dialog. */
const DIALOG_WIDTH_PX = 640;
/** Matches the Insert Cross-reference dialog. */
const DIALOG_HEIGHT_PX = 352;
const BOOKMARK_LIST_HEIGHT_PX = 160;

/** Word sorts by location by default. */
const DEFAULT_SORT_ORDER: BookmarkSortOrder = 'location';

const useStyles = makeStyles()(theme => ({
  bookmarkList: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    height: BOOKMARK_LIST_HEIGHT_PX,
    overflowY: 'auto',
    padding: 0,
  },
  bookmarkListEmpty: {
    color: theme.appColors.secondaryType,
    padding: theme.spacing(1),
  },
  bookmarkListItem: {
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  bookmarksSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  label: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  nameRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(3),
  },
  nameSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  paper: {
    maxWidth: 'calc(100vw - 32px)',
    minHeight: DIALOG_HEIGHT_PX,
    width: DIALOG_WIDTH_PX,
  },
  sortOrder: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
  /** Unlike the other labels, this one sits beside its radio buttons rather than above them. */
  sortOrderLabel: {
    fontWeight: 'bold',
    margin: 0,
  },
}));

export function BookmarkDialog({ handleClose, open }: BookmarkDialogProps) {
  const { classes } = useStyles();
  const editor = useEditor();
  const editingDisabled = useEditingDisabled();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const dispatch = useNotificationDispatch();
  const nameInputId = useId();
  const sortOrderLabelId = useId();

  const [name, setName] = useState('');
  const [selectedMarkerId, setSelectedMarkerId] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<BookmarkSortOrder>(DEFAULT_SORT_ORDER);
  const [isClosing, setIsClosing] = useState(false);

  const { bookmarks } = useBookmarks({ open: open || isClosing, sortOrder });

  const selectedBookmark = bookmarks.find(bookmark => bookmark.markerId === selectedMarkerId);
  const isNameValid = getIsValidBookmarkName(name);
  const isNameTaken = bookmarks.some(bookmark => getIsSameBookmarkName(bookmark.name, name));
  const showInvalidNameError = name !== '' && !isNameValid;
  const showNameTakenError = isNameValid && isNameTaken;
  const canAdd = !editingDisabled && isNameValid && !isNameTaken;

  function handleAdd(): void {
    if (!canAdd) return;

    const inserted = AuthorizedTransforms.insertBookmark(editor, name);
    if (inserted) return handleClose();

    dispatch({
      payload: { message: 'Could not add the bookmark. Please try again.', variant: 'error' },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  function handleDelete(): void {
    if (editingDisabled || !selectedBookmark) return;

    const removed = AuthorizedTransforms.removeBookmark(editor, selectedBookmark.name);
    if (removed) return setSelectedMarkerId(null);

    dispatch({
      payload: { message: 'Could not delete the bookmark. Please try again.', variant: 'error' },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  function handleGoTo(): void {
    if (!selectedBookmark) return;

    const selected = AlineTransforms.selectBookmark(editor, selectedBookmark.name);
    if (selected) return handleClose();

    dispatch({
      payload: { message: 'Could not find the bookmark. Please try again.', variant: 'error' },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  function handleExited(): void {
    setIsClosing(false);
    setName('');
    setSelectedMarkerId(null);
    setSortOrder(DEFAULT_SORT_ORDER);
    ClientTransforms.focus(editor);
  }

  return (
    <AlineDialog
      disableRestoreFocus
      classes={{ paper: classes.paper }}
      maxWidth={false}
      open={open}
      slotProps={{
        transition: { onExit: () => setIsClosing(true), onExited: handleExited },
      }}
      title="Bookmark"
      onClose={handleClose}
    >
      <div className={classes.content}>
        <div className={classes.nameSection}>
          <FormLabel className={classes.label} htmlFor={nameInputId}>
            Bookmark name
          </FormLabel>
          <div className={classes.nameRow}>
            <AlineInput
              autoFocus
              error={showInvalidNameError || showNameTakenError}
              id={nameInputId}
              value={name}
              onChange={event => setName(event.target.value)}
            />
            <AlineButton color="primary" disabled={!canAdd} variant="contained" onClick={handleAdd}>
              Add
            </AlineButton>
          </div>
          {/* Below the row rather than inside the input, so Add stays centered on the input */}
          {showInvalidNameError && (
            <FormHelperText error>
              Valid bookmark names must start with a letter, contain only letters, numbers and
              <br /> underscores, and have a maximum length of {MAX_BOOKMARK_NAME_LENGTH}.
            </FormHelperText>
          )}
          {showNameTakenError && (
            <FormHelperText error>Bookmark names must be unique.</FormHelperText>
          )}
        </div>

        <div className={classes.bookmarksSection}>
          <div className={classes.sortOrder}>
            <FormLabel className={classes.sortOrderLabel} id={sortOrderLabelId}>
              Sort by:
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby={sortOrderLabelId}
              value={sortOrder}
              onChange={(_event, value) => {
                if (isBookmarkSortOrder(value)) setSortOrder(value);
              }}
            >
              {BOOKMARK_SORT_ORDERS.map(sortOrderOption => (
                <FormControlLabel
                  key={sortOrderOption}
                  control={<AlineRadio size="small" />}
                  label={BOOKMARK_SORT_ORDER_LABELS[sortOrderOption]}
                  value={sortOrderOption}
                />
              ))}
            </RadioGroup>
          </div>
          <List
            aria-label="Bookmarks in this document"
            className={classes.bookmarkList}
            component="div"
            role="listbox"
          >
            {bookmarks.length === 0 ? (
              <Typography className={classes.bookmarkListEmpty} variant="body2">
                No bookmarks
              </Typography>
            ) : (
              bookmarks.map(bookmark => {
                const isSelected = bookmark.markerId === selectedBookmark?.markerId;

                return (
                  <ListItemButton
                    key={bookmark.markerId}
                    dense
                    aria-selected={isSelected}
                    className={classes.bookmarkListItem}
                    role="option"
                    selected={isSelected}
                    onClick={() => setSelectedMarkerId(bookmark.markerId)}
                  >
                    {bookmark.name}
                  </ListItemButton>
                );
              })
            )}
          </List>
        </div>
      </div>
      <DialogActions sx={{ justifyContent: 'flex-start', pt: 3, px: 0 }}>
        <AlineButton
          color="primary"
          disabled={editingDisabled || !selectedBookmark}
          variant="outlined"
          onClick={handleDelete}
        >
          Delete
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={!selectedBookmark}
          variant="outlined"
          onClick={handleGoTo}
        >
          Go To
        </AlineButton>
      </DialogActions>
    </AlineDialog>
  );
}
